import React, { useEffect, useRef, useState } from "react";
import Header from "Components/Header/Header";
import Footer from "Components/Footer/Footer";
import LoadVipAccount from "Components/LoadVipAccount";
import BankList from "Components/BankList/BankList";
import { FUND_SOURCE_TYPE, PRODUCTTYPES } from "Utils/Constants";
import useSession from "Utils/Hooks/useSession";
import { faro, LogLevel } from "@grafana/faro-react";
import "./MyAccounts.scss";
import useMode, { MODE } from "Utils/Hooks/useMode";
import useVipApiFetcher from "Utils/Hooks/useVipApiFetcher";
import ProductSwitchModal from "Components/ProductSwitchModal";
import useView, { VIEW } from "Utils/Hooks/useView";
import { useHistory } from "react-router";
import useSessionStorage from "Utils/Hooks/useSessionStorage";
import Loading from "Components/Loading";
import useFundType, { FUNDTYPE } from "Utils/Hooks/useFundType";
import { findDefaultBank } from "Utils/Utils";

const MyAccounts = () => {
  const { session, fetchSession } = useSession();
  const [isLoading, setIsLoading] = useState(false);
  const noWarranty = session.noWarranty;
  const fundType = useFundType();
  const view = useView();
  const history = useHistory();
  const fundingSourceList = useRef(null);
  const [limitAmount, setLimitAmount] = useState(session.depositLimit);
  const [preferredPreviousLimit, setPreferredPreviousLimit] = useState(0);
  const [defaultFund, setDefaultFund] = useState();
  const [preferredCurrentLimit, setPreferredCurrentLimit] = useState(0);
  const operatorDailyLimit = session.depositLimit;
  const isCashierPage = view === VIEW.cashier;
  const patronProductType = session?.patronProductType;
  const patronSelectedProductType = session?.productType;
  const isOperatorPreferred = session?.operatorProducts.includes(
    PRODUCTTYPES.PREFERRED
  );
  const [hasVerifiedBank, setHasVerifiedBanks] = useSessionStorage(
    "has-verified-bank",
    false
  );
  const [unVerifiedBank, setDeclinedVerifiedBank] = useSessionStorage(
    "unverifiedbank",
    false
  );
  const isOperatorOnline = session?.operatorProducts.includes(
    PRODUCTTYPES.ONLINE
  );
  const isOperatorBoth = isOperatorOnline && isOperatorPreferred;
  const [showMessage, setShowMessage] = useState(
    patronProductType !== patronSelectedProductType &&
      isOperatorBoth &&
      !session?.toggles?.productRoutingEnabled
  );
  const [isRepeat, setIsRepeat] = useSessionStorage("isRepeat", false);
  const mode = useMode();
  const vipApi = useVipApiFetcher();
  const manageFundingSources = mode === MODE.manageBanks;
  const fundingSources = useRef([]);
  const productType =
    session?.patronProductType === PRODUCTTYPES.PREFERRED ? "VIPP" : "VIPO";
  const pageTitle = productType + " - My Accounts";
  document.title = pageTitle;
  const title = "My Funding Sources";

  async function onVipOnlineSubmit(event) {
    if (event) event.preventDefault();
    setShowMessage(false);
    if (
      patronProductType === PRODUCTTYPES.PREFERRED &&
      patronSelectedProductType === PRODUCTTYPES.ONLINE
    ) {
      await SubmitProductTypeChange(PRODUCTTYPES.ONLINE);
    }
    setIsRepeat(true);
  }

  async function onVipPreferredSubmit(event) {
    if (event) event.preventDefault();
    setShowMessage(false);
    if (
      patronProductType === PRODUCTTYPES.ONLINE &&
      patronSelectedProductType === PRODUCTTYPES.PREFERRED
    ) {
      await SubmitProductTypeChange(PRODUCTTYPES.PREFERRED);
      setLimitAmount(Math.min(operatorDailyLimit, preferredCurrentLimit));
    }
    setIsRepeat(true);
  }

  async function SubmitProductTypeChange(selectedProductType) {
    setIsLoading(true);
    vipApi
      .fetch("/api/account/switch", "POST", {
        productType: selectedProductType,
      })
      .then(async (response) => {
        if (response.ok) {
          await fetchSession(true);
          let fundList = fundingSourceList.current;
          if (selectedProductType === PRODUCTTYPES.ONLINE) {
            fundList = await vipApi.fetchData("/api/bank/ach");
          }
          if (isOperatorBoth) {
            if (selectedProductType === PRODUCTTYPES.ONLINE) {
              fundList = fundList.filter(
                (fund) =>
                  (fund.achOpenType ===
                    FUND_SOURCE_TYPE.PLAID.toLocaleLowerCase() ||
                    fund.achOpenType === FUND_SOURCE_TYPE.FINICITY) &&
                  fund.availableLimit
              ); // Get only Plaid accounts
              if (fundList.length > 0) {
                setLimitAmount(
                  Math.min(operatorDailyLimit, fundList[0].availableLimit)
                );
              }
              if (!fundList || fundList.length === 0) {
                history.push({
                  pathname: "/addAccount",
                  state: { notRedirect: true },
                });
                return;
              }
            }
          } else if (isOperatorOnline) {
            fundList = fundList.filter(
              (fund) =>
                (fund.achOpenType ===
                  FUND_SOURCE_TYPE.PLAID.toLocaleLowerCase() ||
                  fund.achOpenType === FUND_SOURCE_TYPE.FINICITY) &&
                fund.availableLimit
            ); // Get only Plaid accounts
            if (fundList.length > 0) {
              setLimitAmount(
                Math.min(operatorDailyLimit, fundList[0].availableLimit)
              );
            }
            if (!fundList || fundList.length === 0) {
              history.push({
                pathname: "/addAccount",
                state: { notRedirect: true },
              });
              return;
            }
          }
          fundingSources.current = fundList;
          setDefaultFund(findDefaultBank(fundList));
          setIsLoading(false);
        } else {
          faro.api.pushError(new Error("Product Type Switch Error"), {
            context: response,
          });
          if (response.status === 500) {
            console.error(response.statusText);
            history.push({
              pathname: "/error",
            });
          } else if (response.status === 401) {
            console.error(response.statusText);
            history.push({
              pathname: "/expired",
            });
          } else {
            console.error(response.statusText);
            history.push({
              pathname: "/error",
            });
          }
        }
      })
      .catch((error) => {
        console.error(error);
        faro.api.pushError(new Error("Product Type Switch Network Error"), {
          context: error,
        });
        history.push({
          pathname: "/error",
        });
      });
  }

  useEffect(() => {
    if (!vipApi.ready) return;
    let mounted = true;
    (async () => {
      let fundList = [];
      let vipAccount;
      if (session?.toggles?.productRoutingEnabled) {
        vipAccount = await vipApi.fetchData("/api/account");
        if (vipAccount) {
          setLimitAmount(Math.min(limitAmount, vipAccount.availableAmount));
          setPreferredCurrentLimit(vipAccount.limitAmount);
          setPreferredPreviousLimit(vipAccount.vippPreviousLimit);
        }
      }
      if (fundType === FUNDTYPE.ach) {
        fundList = await vipApi.fetchData("/api/bank/ach"); // ACH only
      } else {
        fundList = await vipApi.fetchData("/api/bank"); // Combined list
      }

      fundingSourceList.current = fundList;
      fundingSources.current = fundList;
      setDefaultFund(findDefaultBank(fundList));

      if (session?.toggles?.productRoutingEnabled) {
        const isVipOnlineLimitHigher =
          vipAccount?.onlineAccountsAvailLimit > vipAccount?.availableAmount;
        if (isOperatorBoth) {
          if (isVipOnlineLimitHigher && !noWarranty) {
            if (patronProductType === PRODUCTTYPES.PREFERRED) {
              // Mark the VIP Preferred Patron as VIP Online
              window.sessionStorage.setItem("onlyPlaid", true);
              await SubmitProductTypeChange(PRODUCTTYPES.ONLINE);
            } else {
              if (
                patronSelectedProductType === PRODUCTTYPES.PREFERRED &&
                patronProductType === PRODUCTTYPES.ONLINE
              ) {
                window.sessionStorage.setItem("onlyPlaid", true);
                await SubmitProductTypeChange(PRODUCTTYPES.ONLINE);
              }
              fundList = fundList.filter(
                (fund) =>
                  (fund.achOpenType ===
                    FUND_SOURCE_TYPE.PLAID.toLocaleLowerCase() ||
                    fund.achOpenType === FUND_SOURCE_TYPE.FINICITY) &&
                  fund.availableLimit
              ); // Get only Plaid accounts
              if (fundList.length > 0) {
                setLimitAmount(
                  Math.min(operatorDailyLimit, fundList[0].availableLimit)
                );
              }

              fundingSources.current = fundList;
              setDefaultFund(findDefaultBank(fundList));
              setIsLoading(false);
            }
          } else {
            if (noWarranty) {
              const hasbanks = fundList.some(
                (fund) =>
                  fund.achOpenType ===
                    FUND_SOURCE_TYPE.PLAID.toLocaleLowerCase() ||
                  fund.achOpenType === FUND_SOURCE_TYPE.FINICITY
              );
              if (hasbanks) {
                setHasVerifiedBanks(hasbanks);
              }
              if (fundList && !hasbanks && unVerifiedBank) {
                faro.api.pushLog(
                  ["Declined to add Verified bank, switching to VIPP"],
                  { level: LogLevel.INFO }
                );
                await SubmitProductTypeChange(PRODUCTTYPES.PREFERRED);
                return;
              } else {
                faro.api.pushLog(
                  [
                    "Nowarranty Deposit, prompting patron to connect a verified bank",
                  ],
                  { level: LogLevel.INFO }
                );
                await SubmitProductTypeChange(PRODUCTTYPES.ONLINE);
                return;
              }
            } else if (
              patronProductType === PRODUCTTYPES.ONLINE ||
              (patronSelectedProductType === PRODUCTTYPES.ONLINE &&
                patronProductType === PRODUCTTYPES.PREFERRED)
            ) {
              await SubmitProductTypeChange(PRODUCTTYPES.PREFERRED);
            } else {
              setIsLoading(false);
            }
          }
        } else if (isOperatorOnline) {
          if (patronProductType === PRODUCTTYPES.PREFERRED) {
            // Mark the VIP Preferred Patron as VIP Online
            window.sessionStorage.setItem("onlyPlaid", true);
            await SubmitProductTypeChange(PRODUCTTYPES.ONLINE);
          } else {
            fundList = fundList.filter(
              (fund) =>
                (fund.achOpenType ===
                  FUND_SOURCE_TYPE.PLAID.toLocaleLowerCase() ||
                  fund.achOpenType === FUND_SOURCE_TYPE.FINICITY) &&
                fund.availableLimit
            ); // Get only Plaid accounts
            if (fundList.length > 0) {
              setLimitAmount(
                Math.min(operatorDailyLimit, fundList[0].availableLimit)
              );
            }

            fundingSources.current = fundList;
            setDefaultFund(findDefaultBank(fundList));
            setIsLoading(false);
          }
        } else if (isOperatorPreferred) {
          if (patronProductType === PRODUCTTYPES.ONLINE) {
            await SubmitProductTypeChange(PRODUCTTYPES.PREFERRED);
          } else {
            setIsLoading(false);
          }
        }
      } else {
        if (
          patronProductType === patronSelectedProductType &&
          patronProductType === PRODUCTTYPES.PREFERRED
        ) {
          setIsLoading(false);
        } else if (
          patronProductType === patronSelectedProductType &&
          patronProductType === PRODUCTTYPES.ONLINE
        ) {
          fundList = fundList.filter(
            (fund) =>
              fund.achOpenType === FUND_SOURCE_TYPE.PLAID.toLocaleLowerCase() ||
              fund.achOpenType === FUND_SOURCE_TYPE.FINICITY //&& fund.availableLimit
          ); // Get only Plaid accounts

          if (fundList.length > 0) {
            setLimitAmount(
              Math.min(operatorDailyLimit, fundList[0].availableLimit)
            );
          }

          fundingSources.current = fundList;
          setDefaultFund(findDefaultBank(fundList));
          setIsLoading(false);
        } else {
          // Requirement document pt.5.c.i
          if (
            isOperatorBoth &&
            patronProductType === PRODUCTTYPES.PREFERRED &&
            patronSelectedProductType === PRODUCTTYPES.ONLINE
          ) {
            if (session.productTypeSwitched) {
              // Mark the VIP Preferred Patron as VIP Online
              window.sessionStorage.setItem("onlyPlaid", true);
              await SubmitProductTypeChange(PRODUCTTYPES.ONLINE);
            } else {
              setIsLoading(false);
            }
          }

          // Requirement document pt.5.c.ii
          if (
            isOperatorBoth &&
            patronProductType === PRODUCTTYPES.ONLINE &&
            patronSelectedProductType === PRODUCTTYPES.PREFERRED
          ) {
            if (session.productTypeSwitched) {
              await SubmitProductTypeChange(PRODUCTTYPES.PREFERRED);
            } else {
              fundList = fundList.filter(
                (fund) =>
                  (fund.achOpenType ===
                    FUND_SOURCE_TYPE.PLAID.toLocaleLowerCase() ||
                    fund.achOpenType === FUND_SOURCE_TYPE.FINICITY) &&
                  fund.availableLimit
              ); // Get only Plaid accounts
              if (fundList.length > 0) {
                setLimitAmount(
                  Math.min(operatorDailyLimit, fundList[0].availableLimit)
                );
              }
              fundingSources.current = fundList;
              setDefaultFund(findDefaultBank(fundList));

              window.sessionStorage.setItem("onlyPlaid", true);
              setIsLoading(false);
            }
          }

          // Requirement document pt.5.c.iii
          if (
            !isOperatorOnline &&
            patronProductType === PRODUCTTYPES.ONLINE &&
            patronSelectedProductType === PRODUCTTYPES.PREFERRED
          ) {
            // Upgrade the VIP Online Patron to VIP Preferred without the pop-up.
            fundingSources.current = fundList;
            setDefaultFund(findDefaultBank(fundList));
            setIsLoading(false);
            await SubmitProductTypeChange(PRODUCTTYPES.PREFERRED);
          }

          // Requirment document pt.5.c.iv
          if (
            !isOperatorPreferred &&
            patronProductType === PRODUCTTYPES.PREFERRED &&
            patronSelectedProductType === PRODUCTTYPES.ONLINE
          ) {
            // Mark the VIP Preferred Patron as VIP Online
            fundList = fundList.filter(
              (fund) =>
                (fund.achOpenType ===
                  FUND_SOURCE_TYPE.PLAID.toLocaleLowerCase() ||
                  fund.achOpenTypeP === FUND_SOURCE_TYPE.FINICITY) &&
                fund.availableLimit
            ); // Get only Plaid accounts
            if (fundList.length > 0) {
              setLimitAmount(
                Math.min(operatorDailyLimit, fundList[0].availableLimit)
              );
            }
            fundingSources.current = fundList;
            setDefaultFund(findDefaultBank(fundList));
            setIsLoading(false);

            window.sessionStorage.setItem("onlyPlaid", true);
            await SubmitProductTypeChange(PRODUCTTYPES.ONLINE);
          }
        }
      }

      if (!mounted) return;
      if (!fundList || fundList.length === 0) {
        history.push({ pathname: "/addAccount", state: { notRedirect: true } });
        return;
      }
    })();
    return () => {
      mounted = false;
    }; // Unmount func
  }, [vipApi.ready]);

  return (
    <>
      <Header />
      {isLoading ? (
        <Loading />
      ) : (
        <div className="my-accounts">
          {manageFundingSources && !isCashierPage
            ? showMessage &&
              !isRepeat &&
              !session.productTypeSwitched && (
                <ProductSwitchModal
                  onVipOnlineSubmit={onVipOnlineSubmit}
                  onVipPreferredSubmit={onVipPreferredSubmit}
                />
              )
            : ""}
          <div className="title text-center">{title}</div>
          <LoadVipAccount>
            <BankList />
          </LoadVipAccount>
        </div>
      )}
      <Footer />
    </>
  );
};

export default MyAccounts;
